/**
 * FolderDataTable.jsx — v10
 * ==========================
 * MATCHES MIS PettyCashPortal exactly:
 *
 *  1. Split-view RecordDetailModal (document preview LEFT + editable fields RIGHT)
 *  2. Category & Project Code CRUD (ManageCategoriesModal)
 *  3. Bulk categorize selected rows (BulkCategorizeModal)
 *  4. Category + Project columns in table
 *  5. Inline click-to-edit cells (EditableCell)
 *  6. Download All as ZIP (server-side)
 *  7. XLSX export (server-side with Category Summary sheet)
 *  8. Row selection checkboxes for bulk ops
 *  9. Edit history / audit trail modal
 * 10. Re-extract single record
 * 11. Re-extract all pending in folder
 *
 * UI: bordered table, NO hover highlight, Excel-cell aesthetic,
 * Segoe UI font, muted colors, professional accounting format.
 * NO colored icons in data cells — plain text only.
 * ALL styling matches MIS PettyCashPortal.jsx verbatim.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table, Tag, Typography, Button, Space, Tooltip, Badge, Empty,
  Spin, Input, Popconfirm, message, Modal,
  Select, Divider, ColorPicker,
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, DeleteOutlined,
  FileTextOutlined, FilePdfOutlined, FileImageOutlined, FileOutlined,
  TagOutlined, DownloadOutlined, EditOutlined, CloseOutlined,
  HistoryOutlined, EyeOutlined,
  SettingOutlined, PlusOutlined, CloudDownloadOutlined,
  ThunderboltOutlined, LoadingOutlined, CheckOutlined,
  ExclamationCircleOutlined, FireOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;
const { Option } = Select;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ── helpers ── */
const getFileIcon = (ext, size = 13) => {
  if (!ext) return <FileTextOutlined style={{ fontSize: size, color: '#888' }} />;
  if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#c41a1a', fontSize: size }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <FileImageOutlined style={{ color: '#b38600', fontSize: size }} />;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileOutlined style={{ color: '#1a7a1a', fontSize: size }} />;
  return <FileTextOutlined style={{ color: '#888', fontSize: size }} />;
};

const S = {
  page:         { fontFamily: "'Segoe UI', Calibri, 'Helvetica Neue', sans-serif", fontSize: 13 },
  cellText:     { fontSize: 12, color: '#1a1a1a' },
  cellBold:     { fontSize: 12, fontWeight: 600, color: '#1a1a1a' },
  cellMuted:    { fontSize: 11, color: '#999' },
  toolbar:      { display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' },
  plainBtn:     { borderRadius: 3, fontSize: 11 },
  link:         { color: '#0958d9', cursor: 'pointer', fontWeight: 600, fontSize: 12, background: 'none', border: 'none', padding: 0 },
  summaryRow:   { display: 'flex', gap: 0, borderBottom: '1px solid #d9d9d9', borderTop: '1px solid #d9d9d9', marginBottom: 16, background: '#fff' },
  summaryCell:  (last) => ({ flex: '1 1 0', padding: '10px 16px', borderRight: last ? 'none' : '1px solid #e8e8e8' }),
  summaryLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 1 },
  summaryValue: (c) => ({ fontSize: 18, fontWeight: 700, color: c || '#1a1a1a', lineHeight: '1.3' }),
  editableCell: { cursor: 'pointer', padding: '2px 4px', borderRadius: 2, minHeight: 20, transition: 'background 0.15s' },
};


/* ═══════════════════════════════════════════
   EDITABLE CELL
   ═══════════════════════════════════════════ */
function EditableCell({ value, recordId, field, onSave, style: cellStyle }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (editing && ref.current) { ref.current.focus(); ref.current.select(); }
  }, [editing]);

  const save = async () => {
    const t = editValue.trim();
    if (t === (value || '').trim()) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(recordId, field, t || null); setEditing(false); }
    catch { message.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
        <input
          ref={ref} value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditValue(value || ''); setEditing(false); } }}
          style={{ fontSize: 12, padding: '1px 6px', borderRadius: 2, border: '1px solid #d9d9d9', flex: 1, minWidth: 60 }}
          disabled={saving}
        />
        <CheckOutlined onClick={save} style={{ fontSize: 12, color: '#52c41a', cursor: 'pointer' }} />
        <CloseOutlined onClick={() => { setEditValue(value || ''); setEditing(false); }} style={{ fontSize: 11, color: '#ff4d4f', cursor: 'pointer' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        ...cellStyle,
        ...S.editableCell,
        ...(hovered ? { background: '#e6f4ff', outline: '1px dashed #91caff' } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={e => { e.stopPropagation(); setEditValue(value || ''); setEditing(true); }}
      title="Click to edit"
    >
      {value || <span style={{ color: '#ccc', fontStyle: 'italic' }}>—</span>}
    </div>
  );
}


/* ═══════════════════════════════════════════
   RECORD DETAIL MODAL (split: preview LEFT + edit RIGHT)
   ═══════════════════════════════════════════ */
function RecordDetailModal({
  visible, record, onClose, onSave, onDelete, onReExtract, reExtracting, deleting,
  previewUrl, previewLoading, onLoadPreview, onDownload, onShowEditLogs,
  categories, projectCodes, onAssignSingle,
}) {
  const [ef, setEf] = useState(null);
  const [ev, setEv] = useState('');
  const [sv, setSv] = useState(false);

  useEffect(() => { setEf(null); setEv(''); }, [record?.id]);

  if (!record) return null;

  const fn = record.renamed_filename || record.original_filename || '';
  const ext = fn.split('.').pop()?.toLowerCase() || '';
  const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  const isPdf = ext === 'pdf';
  const isFuel = fn.toLowerCase().includes('fuel');

  const startE = (f, v) => { setEf(f); setEv(v || ''); };
  const cancelE = () => { setEf(null); setEv(''); };
  const saveE = async () => {
    setSv(true);
    try { await onSave(record.id, ef, ev.trim() || null); setEf(null); setEv(''); }
    catch {} finally { setSv(false); }
  };

  const fields = [
    { key: 'invoice_number', label: 'Invoice No.' },
    { key: 'supplier_name', label: 'Supplier Name' },
    { key: 'invoice_date', label: 'Invoice Date' },
    { key: 'total_amount', label: 'Total Amount (AED)' },
    { key: 'vat_amount', label: 'VAT Amount' },
    { key: 'amount_excl_vat', label: 'Excl. VAT', readonly: true },
    { key: 'supplier_trn', label: 'Supplier TRN' },
    { key: 'buyer_trn', label: 'Buyer TRN' },
    { key: 'vehicle_number', label: 'Vehicle No.' },
    { key: 'supplier_phone', label: 'Phone' },
    { key: 'remarks', label: 'Remarks' },
  ];

  const renderF = (f) => {
    const val = record[f.key];
    const isE = ef === f.key;
    if (f.readonly) return <div style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 500, padding: '4px 0' }}>{val || <span style={{ color: '#ccc' }}>—</span>}</div>;
    if (isE) return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input value={ev} onChange={e => setEv(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') saveE(); if (e.key === 'Escape') cancelE(); }}
          style={{ flex: 1, fontSize: 12, padding: '2px 6px', border: '1px solid #d9d9d9', borderRadius: 2 }}
          autoFocus disabled={sv} />
        <Button size="small" type="primary" icon={<CheckOutlined />} onClick={saveE} loading={sv} style={{ borderRadius: 3, minWidth: 28, padding: '0 6px' }} />
        <Button size="small" icon={<CloseOutlined />} onClick={cancelE} style={{ borderRadius: 3, minWidth: 28, padding: '0 6px' }} />
      </div>
    );
    return (
      <div
        style={{ fontSize: 13, color: val ? '#1a1a1a' : '#ccc', fontWeight: val ? 500 : 400, fontStyle: val ? 'normal' : 'italic', padding: '4px 8px', borderRadius: 3, cursor: 'pointer', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => startE(f.key, val)}
        onMouseEnter={e => e.currentTarget.style.background = '#f0f5ff'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        title="Click to edit"
      >
        <span>{val || '—'}</span>
        <EditOutlined style={{ fontSize: 10, color: '#bfbfbf' }} />
      </div>
    );
  };

  return (
    <Modal open={visible} onCancel={onClose} footer={null} width="92vw"
      style={{ top: 20, maxWidth: 1280 }}
      styles={{ body: { padding: 0, height: 'calc(100vh - 100px)', overflow: 'hidden' } }}
      destroyOnClose closable title={null}>
      <div style={{ display: 'flex', height: '100%' }}>
        {/* LEFT: preview */}
        <div style={{ flex: '1 1 58%', background: '#1e1e1e', display: 'flex', flexDirection: 'column', minWidth: 0, borderRight: '1px solid #d9d9d9' }}>
          <div style={{ padding: '8px 16px', background: '#292929', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #3a3a3a', flexShrink: 0 }}>
            {getFileIcon(ext, 14)}
            <span style={{ fontSize: 12, color: '#ddd', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fn}</span>
            {isFuel && <span style={{ fontSize: 9, fontWeight: 700, color: '#faad14', background: 'rgba(250,173,20,0.15)', padding: '2px 8px', borderRadius: 3 }}>FUEL</span>}
            {record.s3_key && onDownload && (
              <Tooltip title="Download">
                <button onClick={() => onDownload(record.s3_key, fn)} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', padding: 4, fontSize: 14 }}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            {previewLoading ? <Spin size="large" /> :
              previewUrl ? (
                isImg ? <img src={previewUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 12 }} /> :
                isPdf ? <iframe src={previewUrl + '#toolbar=1&navpanes=0'} title="PDF" style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} /> :
                <div style={{ color: '#777', fontSize: 13 }}>Preview not available</div>
              ) :
              record.s3_key ? <Button icon={<EyeOutlined />} onClick={() => onLoadPreview(record.s3_key)} style={{ borderRadius: 4, color: '#ddd', borderColor: '#555', background: '#333' }}>Load Preview</Button> :
              <div style={{ color: '#555' }}>No document</div>
            }
          </div>
        </div>

        {/* RIGHT: fields */}
        <div style={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden', maxWidth: 420 }}>
          <div style={{ padding: '14px 20px', borderBottom: '2px solid #f0f0f0', flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 3 }}>{record.invoice_number || 'No Invoice Number'}</div>
            <div style={{ fontSize: 11, color: '#999' }}>
              {record.supplier_name || 'Unknown'}
              {record.total_amount && <span style={{ marginLeft: 8, fontWeight: 600, color: '#1a5276' }}>{record.total_amount} AED</span>}
            </div>
            {record.category_name && <Tag color={(categories || []).find(c => c.name === record.category_name)?.color || '#8c8c8c'} style={{ marginTop: 6, fontSize: 11 }}>{record.category_name}</Tag>}
            {record.project_code && <Tag style={{ marginTop: 6, fontSize: 11 }}>{record.project_code}</Tag>}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
            {/* Category & Project */}
            <div style={{ marginBottom: 14, padding: '10px 12px', background: '#f8f9fa', borderRadius: 4, border: '1px solid #e8e8e8' }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, fontWeight: 600 }}>Category & Project</div>
              <Select size="small" placeholder="Select category..." value={record.category_id || undefined} style={{ width: '100%', marginBottom: 6 }} onChange={v => onAssignSingle(record.id, v, null)} allowClear onClear={() => onAssignSingle(record.id, 'clear_cat', null)}>
                {(categories || []).map(c => <Option key={c.id} value={c.id}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c.color, marginRight: 6 }} />{c.name}</Option>)}
              </Select>
              <Select size="small" placeholder="Select project..." value={record.project_code_id || undefined} style={{ width: '100%' }} showSearch optionFilterProp="children" onChange={v => onAssignSingle(record.id, null, v)} allowClear onClear={() => onAssignSingle(record.id, null, 'clear_proj')}>
                {(projectCodes || []).map(p => <Option key={p.id} value={p.id}>{p.code} — {p.name}</Option>)}
              </Select>
            </div>
            {fields.map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 2, fontWeight: 600 }}>{f.label}</div>
                {renderF(f)}
              </div>
            ))}
            <div style={{ marginTop: 6, padding: '10px 0', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 10, color: '#bbb', textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 }}>Extraction Info</div>
              <div style={{ fontSize: 11, color: '#888', lineHeight: 1.9 }}>
                <div>Method: <span style={{ color: '#555', fontWeight: 500 }}>{record.extraction_method || record.which_layer_won || '—'}</span></div>
                <div>Confidence: <span style={{ color: '#555', fontWeight: 500 }}>{record.confidence ? `${Math.round(record.confidence * 100)}%` : '—'}</span></div>
              </div>
            </div>
          </div>
          <div style={{ padding: '10px 20px', borderTop: '1px solid #e8e8e8', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, background: '#fafafa' }}>
            <Button size="small" icon={<ThunderboltOutlined />} onClick={() => onReExtract(record.id)} loading={reExtracting} style={{ borderRadius: 3, fontSize: 11, color: '#722ed1', borderColor: '#d3adf7' }}>Re-extract</Button>
            <Button size="small" icon={<HistoryOutlined />} onClick={() => onShowEditLogs(record.id)} style={{ borderRadius: 3, fontSize: 11 }}>History</Button>
            <div style={{ flex: 1 }} />
            <Popconfirm title="Delete?" description="Removes from S3 and DB" icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />} onConfirm={() => onDelete(record)} okText="Delete" okButtonProps={{ danger: true, size: 'small' }}>
              <Button size="small" danger icon={<DeleteOutlined />} loading={deleting} style={{ borderRadius: 3, fontSize: 11 }}>Delete</Button>
            </Popconfirm>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ═══════════════════════════════════════════
   BULK CATEGORIZE MODAL
   ═══════════════════════════════════════════ */
function BulkCategorizeModal({ visible, onClose, selectedCount, categories, projectCodes, onAssign, onCreateCat, onCreateProj }) {
  const [cid, setCid] = useState();
  const [pid, setPid] = useState();
  const [ncn, setNcn] = useState('');
  const [npc, setNpc] = useState('');
  const [npn, setNpn] = useState('');
  const [cr, setCr] = useState(false);

  const doAssign = () => { if (!cid && !pid) { message.warning('Select category or project'); return; } onAssign(cid, pid); };
  const addCat = async () => { if (!ncn.trim()) return; setCr(true); try { const c = await onCreateCat(ncn.trim()); setCid(c.id); setNcn(''); } catch {} finally { setCr(false); } };
  const addProj = async () => { if (!npc.trim()) return; setCr(true); try { const p = await onCreateProj(npc.trim(), npn.trim()); setPid(p.id); setNpc(''); setNpn(''); } catch {} finally { setCr(false); } };

  return (
    <Modal open={visible} onCancel={onClose} title={<span><TagOutlined style={{ marginRight: 8 }} />Categorize {selectedCount} Record{selectedCount > 1 ? 's' : ''}</span>} onOk={doAssign} okText="Assign" okButtonProps={{ disabled: !cid && !pid }} width={480} destroyOnClose>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Category</div>
        <Select size="small" placeholder="Select category..." value={cid} onChange={setCid} style={{ width: '100%' }} allowClear
          dropdownRender={m => <>{m}<Divider style={{ margin: '4px 0' }} /><div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}><Input size="small" placeholder="New category" value={ncn} onChange={e => setNcn(e.target.value)} onPressEnter={addCat} style={{ flex: 1 }} /><Button size="small" type="text" icon={<PlusOutlined />} onClick={addCat} loading={cr}>Add</Button></div></>}>
          {(categories || []).map(c => <Option key={c.id} value={c.id}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c.color, marginRight: 6 }} />{c.name}</Option>)}
        </Select>
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Project Code</div>
        <Select size="small" placeholder="Select project..." value={pid} onChange={setPid} style={{ width: '100%' }} showSearch optionFilterProp="children" allowClear
          dropdownRender={m => <>{m}<Divider style={{ margin: '4px 0' }} /><div style={{ padding: '4px 8px' }}><div style={{ display: 'flex', gap: 4 }}><Input size="small" placeholder="Code" value={npc} onChange={e => setNpc(e.target.value)} style={{ width: 80 }} /><Input size="small" placeholder="Name" value={npn} onChange={e => setNpn(e.target.value)} onPressEnter={addProj} style={{ flex: 1 }} /><Button size="small" type="text" icon={<PlusOutlined />} onClick={addProj} loading={cr}>Add</Button></div></div></>}>
          {(projectCodes || []).map(p => <Option key={p.id} value={p.id}>{p.code} — {p.name}</Option>)}
        </Select>
      </div>
    </Modal>
  );
}


/* ═══════════════════════════════════════════
   MANAGE CATEGORIES & PROJECT CODES MODAL
   ═══════════════════════════════════════════ */
function ManageCategoriesModal({ visible, onClose, categories, projectCodes, onRefresh, onCreateCat, onDeleteCat, onCreateProj }) {
  const [tab, setTab] = useState('categories');
  const [ncn, setNcn] = useState('');
  const [ncc, setNcc] = useState('#1a5276');
  const [npc, setNpc] = useState('');
  const [npn, setNpn] = useState('');
  const [cr, setCr] = useState(false);

  const addCat = async () => { if (!ncn.trim()) { message.warning('Enter name'); return; } setCr(true); try { await onCreateCat(ncn.trim(), ncc); setNcn(''); setNcc('#1a5276'); onRefresh(); } catch {} finally { setCr(false); } };
  const addProj = async () => { if (!npc.trim()) { message.warning('Enter code'); return; } setCr(true); try { await onCreateProj(npc.trim(), npn.trim()); setNpc(''); setNpn(''); onRefresh(); } catch {} finally { setCr(false); } };

  const ts = (a) => ({ padding: '8px 20px', cursor: 'pointer', fontSize: 12, fontWeight: a ? 700 : 500, color: a ? '#1a5276' : '#888', background: 'none', border: 'none', borderBottom: `2px solid ${a ? '#1a5276' : 'transparent'}` });

  return (
    <Modal open={visible} onCancel={onClose} footer={null} width={560} title={<span><SettingOutlined style={{ marginRight: 8 }} />Manage Categories & Project Codes</span>} destroyOnClose>
      <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', marginBottom: 16 }}>
        <button style={ts(tab === 'categories')} onClick={() => setTab('categories')}>Categories</button>
        <button style={ts(tab === 'projects')} onClick={() => setTab('projects')}>Project Codes</button>
      </div>
      {tab === 'categories' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <Input size="small" placeholder="New category name..." value={ncn} onChange={e => setNcn(e.target.value)} onPressEnter={addCat} style={{ flex: 1 }} />
            <ColorPicker size="small" value={ncc} onChange={(_, hex) => setNcc(hex)} />
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={addCat} loading={cr} style={{ borderRadius: 3, background: '#1a5276', borderColor: '#1a5276' }}>Add</Button>
          </div>
          {categories.length === 0 ? <Empty description="No categories yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 4, maxHeight: 320, overflowY: 'auto' }}>
              {categories.map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: i < categories.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: c.color || '#1a5276', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>{c.name}</span>
                  <Popconfirm title={`Deactivate "${c.name}"?`} onConfirm={() => onDeleteCat(c.id, c.name)} okText="Yes" cancelText="No" okButtonProps={{ danger: true, size: 'small' }}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ fontSize: 11 }} />
                  </Popconfirm>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === 'projects' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <Input size="small" placeholder="Code (e.g. PRJ-001)" value={npc} onChange={e => setNpc(e.target.value)} style={{ width: 120 }} />
            <Input size="small" placeholder="Name" value={npn} onChange={e => setNpn(e.target.value)} onPressEnter={addProj} style={{ flex: 1 }} />
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={addProj} loading={cr} style={{ borderRadius: 3, background: '#1a5276', borderColor: '#1a5276' }}>Add</Button>
          </div>
          {projectCodes.length === 0 ? <Empty description="No project codes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 4, maxHeight: 320, overflowY: 'auto' }}>
              {projectCodes.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: i < projectCodes.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1a5276', minWidth: 80 }}>{p.code}</span>
                  <span style={{ flex: 1, fontSize: 12, color: '#555' }}>{p.name || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}


/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const FolderDataTable = ({ folderNumber, moduleType, folderName, username }) => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [exportingXlsx, setExportingXlsx] = useState(false);

  // detail modal
  const [detailVis, setDetailVis] = useState(false);
  const [detailRec, setDetailRec] = useState(null);
  const [detailUrl, setDetailUrl] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reExtId, setReExtId] = useState(null);
  const [reExtFolder, setReExtFolder] = useState(false);

  // edit logs
  const [logVis, setLogVis] = useState(false);
  const [logId, setLogId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logLoad, setLogLoad] = useState(false);

  // categories
  const [cats, setCats] = useState([]);
  const [projs, setProjs] = useState([]);
  const [manageVis, setManageVis] = useState(false);

  // bulk
  const [selKeys, setSelKeys] = useState([]);
  const [bulkVis, setBulkVis] = useState(false);

  // zip
  const [zipLoading, setZipLoading] = useState(false);

  /* ── data fetch ── */
  const fetchData = useCallback(async () => {
    if (!folderNumber || !moduleType) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/folder-data/${moduleType}/${folderNumber}`);
      if (r.data.success) { setData(r.data.records || []); setSummary(r.data.summary || {}); }
    } catch { setData([]); setSummary({}); }
    finally { setLoading(false); }
  }, [folderNumber, moduleType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── categories fetch ── */
  const fetchCatsProjs = useCallback(async () => {
    try {
      const [cr, pr] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/petty-cash/categories`).catch(() => ({ data: { categories: [] } })),
        axios.get(`${API_BASE_URL}/api/petty-cash/project-codes`).catch(() => ({ data: { project_codes: [] } })),
      ]);
      setCats(cr.data?.categories || []);
      setProjs(pr.data?.project_codes || []);
    } catch {}
  }, []);

  useEffect(() => { fetchCatsProjs(); }, [fetchCatsProjs]);

  /* ── cell save ── */
  const cellSave = async (rid, field, val) => {
    const r = await axios.patch(`${API_BASE_URL}/api/folder-data/${rid}`, { [field]: val, edited_by: username || 'unknown' });
    if (r.data.success) {
      message.success(`Updated ${field.replace(/_/g, ' ')}`);
      setData(p => p.map(x => x.id === rid ? { ...x, [field]: val } : x));
      if (detailRec?.id === rid) setDetailRec(p => ({ ...p, [field]: val }));
    } else { message.error(r.data.message || 'Failed'); throw new Error(); }
  };

  /* ── re-extract ── */
  const reExtract = async (rid) => { setReExtId(rid); try { message.info('Re-extraction coming soon on this backend.'); } catch {} finally { setReExtId(null); } };
  const reExtractFolder = async () => { setReExtFolder(true); try { message.info('Folder re-extraction coming soon.'); } catch {} finally { setReExtFolder(false); } };

  /* ── delete ── */
  const deleteRec = async (rec) => {
    setDeleting(true);
    try {
      if (rec.s3_key) try { await axios.delete(`${API_BASE_URL}/api/admin/delete-file`, { params: { key: rec.s3_key } }); } catch {}
      const r = await axios.delete(`${API_BASE_URL}/api/folder-data/${rec.id}`);
      if (r.data.success) { message.success('Deleted'); setData(p => p.filter(x => x.id !== rec.id)); setDetailVis(false); setDetailRec(null); }
    } catch (e) { message.error(e.message || 'Failed'); }
    finally { setDeleting(false); }
  };

  /* ── preview ── */
  const loadPreview = async (k) => {
    setDetailLoading(true);
    try { const r = await axios.get(`${API_BASE_URL}/api/admin/preview-url`, { params: { key: k } }); if (r.data.success) setDetailUrl(r.data.url); }
    catch { message.error('Preview failed'); }
    finally { setDetailLoading(false); }
  };

  const downloadFile = async (k, fn) => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/admin/download-url`, { params: { key: k } });
      if (r.data.success) { const a = document.createElement('a'); a.href = r.data.url; a.download = fn; a.target = '_blank'; a.click(); }
    } catch { message.error('Download failed'); }
  };

  const openDetail = (r) => { setDetailRec(r); setDetailVis(true); setDetailUrl(''); if (r.s3_key) loadPreview(r.s3_key); };

  /* ── edit logs ── */
  const showLogs = async (rid) => {
    setLogId(rid); setLogVis(true); setLogLoad(true);
    try { const r = await axios.get(`${API_BASE_URL}/api/folder-data/${rid}/edit-logs`); setLogs(r.data.success ? r.data.logs || [] : []); }
    catch { setLogs([]); } finally { setLogLoad(false); }
  };

  /* ── category CRUD ── */
  const createCat = async (name, color) => {
    const r = await axios.post(`${API_BASE_URL}/api/petty-cash/categories`, { name, color: color || '#1a5276' });
    if (r.data.success) { message.success(`"${name}" created`); fetchCatsProjs(); return r.data.category; }
    throw new Error();
  };
  const deleteCat = async (id, name) => {
    try { const r = await axios.delete(`${API_BASE_URL}/api/petty-cash/categories/${id}`); if (r.data.success) { message.success(`"${name}" deactivated`); fetchCatsProjs(); } }
    catch { message.error('Failed'); }
  };
  const createProj = async (code, name) => {
    const r = await axios.post(`${API_BASE_URL}/api/petty-cash/project-codes`, { code, name });
    if (r.data.success) { message.success(`"${code}" created`); fetchCatsProjs(); return r.data.project_code; }
    throw new Error();
  };

  /* ── bulk assign ── */
  const bulkAssign = async (cid, pid) => {
    try {
      const b = { record_ids: selKeys };
      if (cid) b.category_id = cid;
      if (pid) b.project_code_id = pid;
      const r = await axios.post(`${API_BASE_URL}/api/petty-cash/assign-category`, b);
      if (r.data.success) { message.success(r.data.message); setBulkVis(false); setSelKeys([]); fetchData(); }
    } catch (e) { message.error(e.message || 'Failed'); }
  };

  /* ── single assign (from detail) ── */
  const assignSingle = async (rid, cid, pid) => {
    try {
      if (cid === 'clear_cat') { await axios.post(`${API_BASE_URL}/api/petty-cash/clear-category`, { record_ids: [rid], clear_category: true }); message.success('Category cleared'); fetchData(); setDetailRec(p => p?.id === rid ? { ...p, category_id: null, category_name: null } : p); return; }
      if (pid === 'clear_proj') { await axios.post(`${API_BASE_URL}/api/petty-cash/clear-category`, { record_ids: [rid], clear_project: true }); message.success('Project cleared'); fetchData(); setDetailRec(p => p?.id === rid ? { ...p, project_code_id: null, project_code: null } : p); return; }
      const b = { record_ids: [rid] };
      if (cid) b.category_id = cid;
      if (pid) b.project_code_id = pid;
      const r = await axios.post(`${API_BASE_URL}/api/petty-cash/assign-category`, b);
      if (r.data.success) {
        message.success('Updated'); fetchData();
        const cn = cid ? (cats.find(c => c.id === cid)?.name || r.data.category) : null;
        const pc = pid ? (projs.find(p => p.id === pid)?.code || r.data.project_code) : null;
        setDetailRec(p => { if (!p || p.id !== rid) return p; const u = { ...p }; if (cid) { u.category_id = cid; u.category_name = cn; } if (pid) { u.project_code_id = pid; u.project_code = pc; } return u; });
      }
    } catch (e) { message.error(e.message || 'Failed'); }
  };

  /* ── server-side XLSX export ── */
  const handleExportXlsx = async () => {
    setExportingXlsx(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/petty-cash/export-xlsx/${moduleType}/${folderNumber}`, { responseType: 'blob', timeout: 60000 });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PettyCash_${folderNumber}_${moduleType}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      message.success('Exported');
    } catch { message.error('Export failed'); }
    finally { setExportingXlsx(false); }
  };

  /* ── ZIP download ── */
  const handleDownloadZip = async () => {
    setZipLoading(true);
    message.loading({ content: 'Preparing ZIP...', key: 'zip', duration: 0 });
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/petty-cash/download-folder-zip/${moduleType}/${folderNumber}`, { responseType: 'blob', timeout: 300000 });
      const blob = new Blob([resp.data], { type: 'application/zip' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Folder_${folderNumber}_${moduleType}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      const included = resp.headers['x-files-included'] || '?';
      const failed = resp.headers['x-files-failed'] || '0';
      message.success({ content: `Downloaded ${included} files${failed !== '0' ? ` (${failed} failed)` : ''}`, key: 'zip' });
    } catch { message.error({ content: 'ZIP download failed', key: 'zip' }); }
    finally { setZipLoading(false); }
  };

  /* ── filtered data ── */
  const filteredData = data.filter(r => {
    if (!searchText.trim()) return true;
    const s = searchText.toLowerCase();
    return [r.invoice_number, r.supplier_name, r.total_amount, r.supplier_trn, r.vehicle_number, r.category_name, r.project_code, r.fuel_name, r.renamed_filename, r.original_filename, r.remarks].some(f => f && String(f).toLowerCase().includes(s));
  });

  const pendingCount = data.filter(r => !r.invoice_number && r.which_layer_won === 'pending').length;
  const hasAnyFuel = data.some(r => r.fuel_serial_no);
  const rowSelection = { selectedRowKeys: selKeys, onChange: setSelKeys, columnWidth: 36 };

  /* ── columns ── */
  const cols = useMemo(() => {
    const c = [
      { title: '#', key: 'i', width: 36, align: 'center', render: (_, __, i) => <span style={S.cellMuted}>{i + 1}</span> },
      { title: 'Invoice No.', dataIndex: 'invoice_number', key: 'inv', width: 140, render: (t, r) => <EditableCell value={t} recordId={r.id} field="invoice_number" onSave={cellSave} style={t ? S.cellBold : { ...S.cellMuted, fontStyle: 'italic' }} /> },
      { title: 'Supplier', dataIndex: 'supplier_name', key: 'sup', width: 200, render: (t, r) => <EditableCell value={t} recordId={r.id} field="supplier_name" onSave={cellSave} style={S.cellText} /> },
      { title: 'Date', dataIndex: 'invoice_date', key: 'dt', width: 100, render: (t, r) => <EditableCell value={t} recordId={r.id} field="invoice_date" onSave={cellSave} style={S.cellText} /> },
      { title: 'Total', dataIndex: 'total_amount', key: 'amt', width: 100, align: 'right', render: (t, r) => <EditableCell value={t} recordId={r.id} field="total_amount" onSave={cellSave} style={t ? S.cellBold : S.cellMuted} /> },
      { title: 'VAT', dataIndex: 'vat_amount', key: 'vat', width: 80, align: 'right', render: (t, r) => <EditableCell value={t} recordId={r.id} field="vat_amount" onSave={cellSave} style={S.cellText} /> },
      { title: 'Vehicle', dataIndex: 'vehicle_number', key: 'veh', width: 90, render: (t, r) => <EditableCell value={t} recordId={r.id} field="vehicle_number" onSave={cellSave} style={S.cellText} /> },
      { title: 'TRN', dataIndex: 'supplier_trn', key: 'trn', width: 140, render: (t, r) => <EditableCell value={t} recordId={r.id} field="supplier_trn" onSave={cellSave} style={S.cellText} /> },
    ];

    if (hasAnyFuel) {
      c.push({ title: 'Fuel', dataIndex: 'fuel_name', key: 'fuel', width: 160, render: (t, r) => r.fuel_serial_no ? <span style={{ fontSize: 11, fontWeight: 600, color: '#b38600' }}>{r.fuel_serial_no} {t || ''}</span> : <span style={S.cellMuted}>—</span> });
    }

    c.push(
      { title: 'Category', dataIndex: 'category_name', key: 'cat', width: 110, render: t => t ? <Tag color={(cats || []).find(x => x.name === t)?.color || '#8c8c8c'} style={{ fontSize: 10, margin: 0 }}>{t}</Tag> : <span style={S.cellMuted}>—</span> },
      { title: 'Project', dataIndex: 'project_code', key: 'proj', width: 80, render: t => t ? <span style={{ ...S.cellText, fontWeight: 600 }}>{t}</span> : <span style={S.cellMuted}>—</span> },
      {
        title: '', key: 'act', width: 60, align: 'center',
        render: (_, r) => (
          <Space size={4}>
            <Tooltip title="View"><button style={{ ...S.link, color: '#1a5276' }} onClick={e => { e.stopPropagation(); openDetail(r); }}><EyeOutlined style={{ fontSize: 13 }} /></button></Tooltip>
            <Tooltip title="Re-extract"><button style={{ ...S.link, color: reExtId === r.id ? '#999' : '#722ed1' }} onClick={e => { e.stopPropagation(); reExtract(r.id); }} disabled={reExtId === r.id}>{reExtId === r.id ? <LoadingOutlined style={{ fontSize: 11 }} /> : <ThunderboltOutlined style={{ fontSize: 11 }} />}</button></Tooltip>
          </Space>
        ),
      },
    );

    return c;
  }, [cats, hasAnyFuel, reExtId, data]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── RENDER ── */
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>Folder {folderName || folderNumber} — Invoice Records</div>
          <div style={{ fontSize: 11.5, color: '#777', marginTop: 1 }}>{data.length} records · {moduleType === 'site-petty-cash' ? 'Site Petty Cash' : 'Procurement PC'}</div>
        </div>
        <Button size="small" icon={<SettingOutlined />} onClick={() => setManageVis(true)} style={{ borderRadius: 3, fontSize: 11 }}>Manage Categories</Button>
      </div>

      {/* Summary */}
      {data.length > 0 && (
        <div style={S.summaryRow}>
          <div style={S.summaryCell(false)}><div style={S.summaryLabel}>Records</div><div style={S.summaryValue()}>{summary.total_records || data.length}</div></div>
          <div style={S.summaryCell(false)}><div style={S.summaryLabel}>Detected</div><div style={S.summaryValue('#1a7a1a')}>{summary.invoices_detected || 0}</div></div>
          <div style={S.summaryCell(false)}><div style={S.summaryLabel}>With Amount</div><div style={S.summaryValue('#0958d9')}>{summary.has_amount || 0}</div></div>
          <div style={S.summaryCell(true)}><div style={S.summaryLabel}>Pending</div><div style={S.summaryValue(pendingCount > 0 ? '#d48806' : '#52c41a')}>{pendingCount}</div></div>
        </div>
      )}

      {/* Toolbar */}
      <div style={S.toolbar}>
        <Input placeholder="Search..." prefix={<SearchOutlined />} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear style={{ maxWidth: 220, borderRadius: 3 }} size="small" />
        {selKeys.length > 0 && <Button size="small" icon={<TagOutlined />} onClick={() => setBulkVis(true)} style={{ borderRadius: 3, fontSize: 11, color: '#1a5276', borderColor: '#7fb3d3', background: '#e8f4fd' }}>Categorize {selKeys.length}</Button>}
        <Button size="small" style={S.plainBtn} icon={<DownloadOutlined />} onClick={handleExportXlsx} loading={exportingXlsx} disabled={!data.length}>Excel Report</Button>
        <Button size="small" style={{ ...S.plainBtn, color: '#1a5276', borderColor: '#7fb3d3' }} icon={<CloudDownloadOutlined />} onClick={handleDownloadZip} loading={zipLoading} disabled={!data.length}>Download All</Button>
        {pendingCount > 0 && (
          <Popconfirm title={`Re-extract ${pendingCount}?`} onConfirm={reExtractFolder} okText="Yes">
            <Button size="small" type="primary" loading={reExtFolder} style={{ borderRadius: 3, fontSize: 11, background: '#722ed1', borderColor: '#722ed1' }} icon={<ThunderboltOutlined />}>Re-extract {pendingCount}</Button>
          </Popconfirm>
        )}
        <Button size="small" style={{ ...S.plainBtn, marginLeft: 'auto' }} onClick={fetchData} loading={loading} icon={<ReloadOutlined />}>Refresh</Button>
      </div>

      {data.length > 0 && <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Select rows to bulk categorize · Click cell to edit · Double-click row for details</div>}

      {/* Table */}
      {loading ? <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div> :
        !data.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data — upload documents" /> :
        <Table
          columns={cols}
          dataSource={filteredData.map((r, i) => ({ ...r, key: r.id || i }))}
          rowSelection={rowSelection}
          pagination={filteredData.length > 20 ? { pageSize: 20, size: 'small' } : false}
          scroll={{ x: hasAnyFuel ? 1450 : 1300 }}
          size="small"
          bordered
          rowClassName={() => 'pc-row'}
          onRow={rec => ({ onDoubleClick: () => openDetail(rec) })}
        />
      }

      {/* MODALS */}
      <RecordDetailModal
        visible={detailVis} record={detailRec}
        onClose={() => { setDetailVis(false); setDetailRec(null); setDetailUrl(''); }}
        onSave={cellSave} onDelete={deleteRec} onReExtract={reExtract}
        reExtracting={reExtId === detailRec?.id} deleting={deleting}
        previewUrl={detailUrl} previewLoading={detailLoading}
        onLoadPreview={loadPreview} onDownload={downloadFile} onShowEditLogs={showLogs}
        categories={cats} projectCodes={projs} onAssignSingle={assignSingle}
      />
      <BulkCategorizeModal visible={bulkVis} onClose={() => setBulkVis(false)} selectedCount={selKeys.length} categories={cats} projectCodes={projs} onAssign={bulkAssign} onCreateCat={n => createCat(n)} onCreateProj={(c, n) => createProj(c, n)} />
      <ManageCategoriesModal visible={manageVis} onClose={() => setManageVis(false)} categories={cats} projectCodes={projs} onRefresh={fetchCatsProjs} onCreateCat={createCat} onDeleteCat={deleteCat} onCreateProj={createProj} />

      {/* Edit logs */}
      <Modal title={`Edit History — #${logId}`} open={logVis} onCancel={() => { setLogVis(false); setLogs([]); }} footer={null} width={600}>
        {logLoad ? <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div> : !logs.length ? <Empty description="No history" /> :
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>{logs.map((l, i) => (
            <div key={l.id || i} style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 12, display: 'flex', gap: 12 }}>
              <div style={{ flex: '0 0 120px' }}>
                <div style={{ fontSize: 11, color: '#888' }}>{l.edited_at ? new Date(l.edited_at).toLocaleString() : '—'}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>{l.edited_by || '?'}</div>
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, color: '#1a5276' }}>{(l.field_name || '').replace(/_/g, ' ')}</span>
                <div style={{ marginTop: 2 }}>
                  <span style={{ color: '#c41a1a', textDecoration: 'line-through', fontSize: 11 }}>{l.old_value || '(empty)'}</span>
                  <span style={{ color: '#888', margin: '0 6px' }}>→</span>
                  <span style={{ color: '#237804', fontWeight: 500, fontSize: 11 }}>{l.new_value || '(empty)'}</span>
                </div>
              </div>
            </div>
          ))}</div>
        }
      </Modal>

      <style>{`
        .pc-row td { border-bottom: 1px solid #e8e8e8 !important; }
        .pc-row:hover > td { background: transparent !important; }
        .ant-table-bordered .ant-table-thead > tr > th {
          background: #f5f5f5 !important;
          font-weight: 600 !important;
          font-size: 11.5px !important;
          color: #333 !important;
          border-bottom: 2px solid #d9d9d9 !important;
          padding: 6px 8px !important;
        }
        .ant-table-bordered .ant-table-tbody > tr > td {
          padding: 4px 6px !important;
          font-size: 12px !important;
        }
        .ant-table-bordered .ant-table-tbody > tr:hover > td { background: transparent !important; }
        .ant-table-cell-row-hover { background: transparent !important; }
      `}</style>
    </div>
  );
};

export default FolderDataTable;